// @ts-nocheck
import { createFromProduct } from "@insite/client-framework/Common/ProductInfo";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    chain,
    LoadRealTimeInventory as BaseLoadRealTimeInventory,
    LoadRealTimePrices as BaseLoadRealTimePrices,
} from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/LoadProductPricingAndInventory";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import {
    getProductState,
    getVariantChildrenDataView,
} from "@store/Modules/client-framework/src/Store/Data/Products/ProductsSelectors";
import { createChainHandler, replaceInChain } from "@toolkit/Modules/client-framework/HandlerCreator";

export const LoadRealTimePrices = createChainHandler("LoadRealTimePrices", chain, async props => {
    if (!props.parameter.product || !props.parameter.productInfosById) {
        return;
    }

    /* Store Custom Code - Start */
    const info = props.parameter.productInfosById[props.parameter.product.id];
    if (!info) {
        return;
    }

    const productIds = [info.productId];

    const productPriceParameters = [
        {
            productId: info.productId,
            unitOfMeasure: info.unitOfMeasure,
            qtyOrdered: info.qtyOrdered,
        },
    ];
    /* Store Custom Code - End */

    props.dispatch(
        loadRealTimePricing({
            productPriceParameters,
            onComplete: pricingProps => {
                const results = pricingProps.apiResult?.realTimePricingResults; // Store Ciustom Code

                /* Store Custom Code - Start */
                if (results) {
                    const found = results.find(o => o.productId === info.productId);

                    if (found) {
                        props.dispatch({
                            type: "Pages/ProductDetails/CompleteLoadRealTimePricings",
                            pricings: results, // Store Custom Code
                        });
                    } else {
                        props.dispatch({
                            type: "Pages/ProductDetails/FailedLoadRealTimePricings",
                            productIds,
                        });
                    }
                } else if (pricingProps.error) {
                    props.dispatch({
                        type: "Pages/ProductDetails/FailedLoadRealTimePricings",
                        productIds,
                    });
                }
                /* Store Custom Code - End */

                props.pricingLoaded = true;
            },
        }),
    );

    if (getSettingsCollection(props.getState()).productSettings.inventoryIncludedWithPricing) {
        await waitFor(() => !!props.pricingLoaded);
    }
});

replaceInChain(chain, BaseLoadRealTimePrices, LoadRealTimePrices);

export const LoadRealTimeInventory = createChainHandler("LoadRealTimeInventory", chain, props => {
    if (
        !props.parameter.product ||
        (getIsWebCrawler() &&
            !getSettingsCollection(props.getState()).websiteSettings.includeRealTimeInventoryAndPricingForSeo)
    ) {
        return;
    }
    const productId = props.parameter.product.id; // Store Custom Code

    props.dispatch(
        loadRealTimeInventory({
            productIds: [productId], // Store Custom Code
            onComplete: realTimeInventoryProps => {
                const realTimeInventory = realTimeInventoryProps?.apiResult;

                if (realTimeInventoryProps?.error) {
                    if (props.parameter.product) {
                        props.dispatch({
                            type: "Pages/ProductDetails/FailedLoadRealTimeInventory",
                            productId: props.parameter.product.id,
                        });
                    }
                } else if (realTimeInventory) {
                    if (
                        realTimeInventory.realTimeInventoryResults &&
                        props.parameter.variantChildren &&
                        props.parameter.product
                    ) {
                        const discontinued: SafeDictionary<boolean> = {};
                        for (const result of realTimeInventory.realTimeInventoryResults) {
                            if (!result.qtyOnHand) {
                                discontinued[result.productId] = true;
                            }
                        }
                        const initialCount = props.parameter.variantChildren.length;
                        const newVariantChildren = props.parameter.variantChildren.filter(
                            o => !o.isDiscontinued || (o.isDiscontinued && !discontinued[o.id]),
                        );

                        if (initialCount !== newVariantChildren.length) {
                            const variantChildrenDataView = getVariantChildrenDataView(
                                props.getState(),
                                props.parameter.product, // Store Custom Code
                            );
                            const { value, ...otherProps } = variantChildrenDataView;
                            props.dispatch({
                                type: "Data/Products/ReplaceDataView",
                                parameter: {
                                    productId: props.parameter.product.id,
                                    variantChildren: true,
                                },
                                dataView: {
                                    value: newVariantChildren,
                                    ...otherProps,
                                },
                            });

                            const productInfo =
                                props.getState().pages.productDetails.productInfosById?.[props.parameter.product.id] ??
                                createFromProduct(
                                    getProductState(props.getState(), props.parameter.product.id).value ??
                                        props.parameter.product,
                                );

                            props.dispatch({
                                type: "Pages/ProductDetails/UpdateVariantSelection",
                                variantSelection: getVariantSelection(
                                    props.parameter.product,
                                    newVariantChildren,
                                    props.parameter.styledOption,
                                ),
                                variantSelectionCompleted: false,
                                selectedProductInfo: productInfo,
                            });
                        }
                    }

                    props.dispatch({
                        type: "Pages/ProductDetails/CompleteLoadRealTimeInventory",
                        realTimeInventory,
                    });
                }

                props.inventoryLoaded = true;
            },
        }),
    );
});

replaceInChain(chain, BaseLoadRealTimeInventory, LoadRealTimeInventory);

function getVariantSelection(product?: ProductModel, variantChildren?: ProductModel[], styledOption?: string) {
    const variantSelection: SafeDictionary<string> = {};
    if (!product?.isVariantParent || !variantChildren) {
        return variantSelection;
    }

    if (styledOption || product.defaultChildProductId) {
        const variantChild = styledOption
            ? variantChildren.find(o => o.productNumber.toLowerCase() === styledOption.toLowerCase())
            : variantChildren.find(o => o.id === product.defaultChildProductId);

        variantChild?.childTraitValues?.forEach(o => {
            variantSelection[o.styleTraitId] = o.id;
        });

        if (Object.keys(variantSelection).length > 0) {
            return variantSelection;
        }
    }

    product.variantTraits?.forEach(variantTrait => {
        const defaultVariantValue = variantTrait.traitValues?.find(traitValue => traitValue.isDefault);
        variantSelection[variantTrait.id] = defaultVariantValue?.id ?? "";
    });

    return variantSelection;
}
